import {
  CATEGORIES,
  CATEGORY_LABELS,
  DESTINATION_LABELS,
  MODE_DESCRIPTIONS,
  MODE_LABELS,
  type EmailCategory,
  type TriageDestination,
  type TriageMode,
} from '@/shared/triage/vocabulary';
import { Badge } from '@/shared/ui/Badge';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { LockIcon } from '@/shared/ui/icons';

import { modesByMailbox, type TriageActivation } from './types';
import { useActivation } from './useActivation';

const HELP = [
  <>
    <strong>Solo propone</strong>: clasifica y propone, sin tocar el correo.{' '}
    <strong>Al confirmar</strong>: actúa cuando alguien pulsa «Correcto» o «Corregir a…» en la
    Bandeja. <strong>Automático</strong>: actúa en cuanto clasifica el correo, sin que nadie lo
    revise.
  </>,
  <>
    <strong>Reenviar</strong> es mandar el correo, tal cual llegó, a la persona que le toca.{' '}
    <strong>Copiar a carpeta</strong> es dejar una copia en una carpeta del propio buzón; el
    original se queda en la bandeja de entrada.
  </>,
  <>
    Pasar una categoría a «Automático» es una decisión escrita de Cubas (cláusula 6.3 del contrato),
    de una en una, empezando por la que más acierta.
  </>,
  <>
    Esta pantalla no cambia nada: los modos se editan desde Administración y el cerrojo lo abre
    quien administra la aplicación.
  </>,
];

/**
 * What the triage is allowed to do, where, and how much on its own (contract v10).
 *
 * Three modes per mailbox and category, two knobs (redirect, copy to folder). Phase 1
 * starts with redirects «al confirmar» and copies «solo»: the system proposes, the copy
 * lands in the folder, and nothing leaves the mailbox until a person presses Correcto.
 * The lock stays outside the application, in the server's configuration: the one control
 * that has to work when this screen does not. Read only, and it says so.
 */
export function ActivationPage() {
  const activation = useActivation();

  return (
    <>
      <PageHeader
        title="Activación"
        badge={
          <Badge tone="neutral">
            <LockIcon size={13} /> solo lectura
          </Badge>
        }
        description="Cuánto hace el sistema por su cuenta con el correo, buzón por buzón y categoría por categoría."
        help={HELP}
      />

      {activation.isPending ? <Spinner variant="block" label="Cargando la activación" /> : null}
      {activation.isError ? (
        <ErrorMessage error={activation.error}>No se pudo cargar la activación</ErrorMessage>
      ) : null}
      {activation.data ? <ActivationView activation={activation.data} /> : null}
    </>
  );
}

function ModeBadge({ mode }: { mode: TriageMode }) {
  const tone = mode === 'auto' ? 'warn' : mode === 'confirm' ? 'accent' : 'neutral';
  return (
    <Badge tone={tone} title={MODE_DESCRIPTIONS[mode]}>
      {MODE_LABELS[mode]}
    </Badge>
  );
}

function ActivationView({ activation }: { activation: TriageActivation }) {
  const grouped = modesByMailbox(activation.modes ?? []);
  const destinations = Object.entries(activation.destinations ?? {}) as [
    TriageDestination,
    string[],
  ][];
  const folders = Object.entries(activation.folders ?? {}) as [EmailCategory, string][];

  return (
    <>
      <Callout
        tone={activation.enabled ? 'warn' : 'info'}
        title={activation.enabled ? 'El triaje puede actuar' : 'El triaje está en sombra'}
        role="status"
        compact
      >
        {activation.enabled
          ? 'El cerrojo está abierto: cada categoría hace lo que dice su modo abajo.'
          : 'El cerrojo está cerrado: el sistema clasifica y propone un destino, pero no reenvía ni copia nada, digan lo que digan los modos.'}
      </Callout>

      {!activation.requires_authenticated_sender ? (
        <Callout
          tone="danger"
          title="Ojo: está desactivada la comprobación de autenticación del remitente"
        >
          Dos reglas de triaje se disparan con cabeceras que escribe quien manda el correo, así que
          sin esa comprobación un remitente falsificado puede elegir a dónde se reenvía su mensaje.
        </Callout>
      ) : null}

      <p className="text-ink-3 text-[13.5px]">
        Por defecto: reenviar <strong>«{MODE_LABELS[activation.default_forward_mode]}»</strong> y
        copiar a carpeta <strong>«{MODE_LABELS[activation.default_label_mode]}»</strong>. Los modos
        se cambian desde el panel de administración; el cerrojo, quien administra la aplicación.
      </p>

      <Card title="Modo por buzón y categoría" {...(grouped.size === 0 ? {} : { padded: false })}>
        {grouped.size === 0 ? (
          <p className="text-ink-3 text-[14px]">
            Ningún buzón registrado todavía. Cuando lo haya, cada categoría aparecerá aquí con su
            modo de reenvío y de copia a carpeta.
          </p>
        ) : (
          <Table label="Modo por buzón y categoría" minWidth="0">
            <THead>
              <tr>
                <Th>Categoría</Th>
                <Th className="w-40">Reenviar</Th>
                <Th className="w-40">Copiar a carpeta</Th>
              </tr>
            </THead>
            {[...grouped].map(([mailbox, rows]) => (
              <TBody key={mailbox}>
                <tr className="bg-paper-2/50">
                  <th
                    scope="colgroup"
                    colSpan={3}
                    className="text-ink px-3 py-1.5 text-left text-[13.5px] font-semibold"
                  >
                    {mailbox}
                  </th>
                </tr>
                {CATEGORIES.map((category) => {
                  const row = rows.get(category);
                  if (!row) return null;
                  return (
                    <Tr key={category}>
                      <Td className="py-1.5 text-[13.5px]">{CATEGORY_LABELS[category]}</Td>
                      <Td className="py-1.5">
                        <ModeBadge mode={row.forward_mode} />
                      </Td>
                      <Td className="py-1.5">
                        <ModeBadge mode={row.label_mode} />
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            ))}
          </Table>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="A dónde va cada destino">
          {destinations.length === 0 ? (
            <p className="text-ink-3 text-[14px]">
              Sin direcciones configuradas. Mientras esté así no se puede reenviar nada, aunque todo
              lo demás esté encendido.
            </p>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-[14px]">
              {destinations.map(([destination, addresses]) => (
                <div key={destination} className="contents">
                  <dt className="text-ink-3">{DESTINATION_LABELS[destination]}</dt>
                  <dd className="text-ink min-w-0 break-words">{addresses.join(', ')}</dd>
                </div>
              ))}
            </dl>
          )}
        </Card>

        <Card title="En qué carpeta se copia cada categoría">
          {folders.length === 0 ? (
            <p className="text-ink-3 text-[14px]">Sin carpetas configuradas.</p>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-[14px]">
              {folders.map(([category, folder]) => (
                <div key={category} className="contents">
                  <dt className="text-ink-3">{CATEGORY_LABELS[category]}</dt>
                  <dd className="text-ink">{folder}</dd>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>
    </>
  );
}
